import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Briefcase, Gavel, FileText, Receipt, LogOut, Menu, X, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
    { to: "/portal", label: "Overview", icon: Home, end: true, testId: "portal-nav-overview" },
    { to: "/portal/cases", label: "My Cases", icon: Briefcase, testId: "portal-nav-cases" },
    { to: "/portal/chat", label: "Chat with my Lawyer", icon: MessageSquare, testId: "portal-nav-chat" },
    { to: "/portal/hearings", label: "Hearings", icon: Gavel, testId: "portal-nav-hearings" },
    { to: "/portal/documents", label: "Documents", icon: FileText, testId: "portal-nav-documents" },
    { to: "/portal/invoices", label: "Invoices", icon: Receipt, testId: "portal-nav-invoices" },
];

export default function PortalLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [firm, setFirm] = useState(null);

    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
    useEffect(() => {
        api.get("/portal/me").then(r => setFirm(r.data.firm)).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <aside className={`fixed top-0 left-0 h-screen w-[240px] bg-background border-r border-border flex flex-col z-40 transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
                <div className="px-6 h-16 flex items-center justify-between border-b border-border">
                    <Link to="/portal" className="flex items-center gap-2.5" data-testid="portal-logo">
                        <div className="w-7 h-7 border border-primary flex items-center justify-center">
                            <span className="font-serif text-primary text-sm">I</span>
                        </div>
                        <div>
                            <div className="font-serif text-[14px] leading-none truncate max-w-[140px]">{firm?.name || "Client Portal"}</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">Client Portal</div>
                        </div>
                    </Link>
                    <button onClick={() => setDrawerOpen(false)} className="lg:hidden text-muted-foreground"><X className="w-5 h-5" strokeWidth={1.5}/></button>
                </div>
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
                    {NAV.map(item => (
                        <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testId}
                            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? "bg-muted text-foreground border-l-2 border-primary pl-[10px]" : "text-muted-foreground hover:text-foreground hover:bg-muted border-l-2 border-transparent"
                            }`}>
                            <item.icon className="w-4 h-4" strokeWidth={1.5}/><span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-border">
                    <div className="px-3 py-2 mb-2">
                        <div className="text-sm truncate">{user?.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                    </div>
                    <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="portal-logout-btn"
                        className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-2">
                        <LogOut className="w-4 h-4" strokeWidth={1.5}/> Sign out
                    </button>
                </div>
            </aside>

            {drawerOpen && <div className="fixed inset-0 bg-background/60 z-30 lg:hidden" onClick={() => setDrawerOpen(false)}></div>}

            <header className="fixed top-0 left-0 lg:left-[240px] right-0 h-16 backdrop-blur-xl bg-background/85 border-b border-border z-20 flex items-center justify-between px-4 sm:px-6">
                <div className="flex items-center gap-3">
                    <button onClick={() => setDrawerOpen(true)} data-testid="portal-open-sidebar" className="lg:hidden text-foreground p-2"><Menu className="w-5 h-5" strokeWidth={1.5}/></button>
                    <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-muted-foreground">Welcome</div>
                </div>
                <div className="flex items-center gap-3">
                    <NotificationBell/>
                    <ThemeToggle/>
                    <span className="text-xs text-muted-foreground truncate max-w-[140px] sm:max-w-none">{user?.email}</span>
                </div>
            </header>

            <main className="lg:ml-[240px] pt-16 min-h-screen">
                <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]"><Outlet /></div>
            </main>
        </div>
    );
}
